import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from 'views/components';
import {Translate, translate} from 'views/module';

import ViewEditBio from 'views/scenes/caregiver/detail/caregiver_scenes/caregiver_feed/biography/view_edit/ViewEditBio';
import Sections from 'views/scenes/caregiver/detail/caregiver_scenes/caregiver_feed/biography/sections';

import {hydateQestions, simplifyQuestions} from 'utils/user_bio';
import backend from 'utils/backend';

const ProfileSection = (props) => {
  const {
    bioQuestions,
    caregiverRn,
    EditComponent,
    getSignedFileUrl,
    saveBioQuestions,
    title,
    ViewComponent,
  } = props;

  return (
    <ViewEditBio
      bioQuestions={bioQuestions}
      caregiverRn={caregiverRn}
      editDisabled={!EditComponent}
      EditComponent={EditComponent}
      getSignedFileUrl={getSignedFileUrl}
      saveBioQuestions={saveBioQuestions}
      title={title}
      ViewComponent={ViewComponent}
    />
  );
};

ProfileSection.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  caregiverRn: PropTypes.string.isRequired,
  EditComponent: PropTypes.elementType,
  getSignedFileUrl: PropTypes.func.isRequired,
  saveBioQuestions: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  ViewComponent: PropTypes.elementType.isRequired,
};

const ContactSection = (props) => {
  const {bioQuestions} = props;
  const fullName = [bioQuestions.firstName, bioQuestions.lastName].filter(Boolean).join(' ');
  const address = [
    bioQuestions.streetAddress,
    bioQuestions.city,
    bioQuestions.province,
    bioQuestions.postalCode,
  ].filter(Boolean).join(', ');

  const details = [
    fullName,
    bioQuestions.email,
    bioQuestions.phoneNumber,
    address,
  ];

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography variant="h6" sx={{fontWeight: 700}}>
        <Translate expression="caregiver.CaregiverBiography.Contact.Title" />
      </Typography>
      <Divider sx={{my: 1.5}} />
      <Stack spacing={0.75}>
        {
          details.filter(Boolean).map((detail, index) => (
            <Typography key={index}>{detail}</Typography>
          ))
        }
        {
          details.every((detail) => !detail) &&
          <Typography color="text.secondary">
            <Translate expression="career.jobDetail.CaregiverQuickView.NoProfileInformation" />
          </Typography>
        }
      </Stack>
    </Box>
  );
};

ContactSection.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
};

const CaregiverProfileDialog = (props) => {
  const {caregiverName, caregiverRn, initialBio, onClose, open} = props;
  const [caregiver, setCaregiver] = React.useState(null);
  const [bioQuestions, setBioQuestions] = React.useState(initialBio || null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    backend.caregiver.getCaregiver(caregiverRn, undefined, true)
        .then((result) => {
          if (cancelled) return;

          setCaregiver(result?.caregiverDetail || null);
          setBioQuestions(simplifyQuestions(result?.caregiverBio?.questions || {}));
        })
        .catch(() => {
          if (!cancelled) setHasError(true);
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });

    return () => {
      cancelled = true;
    };
  }, [caregiverRn, open]);

  const saveBioQuestions = (fields, onSuccess) => {
    const questions = hydateQestions(fields);
    backend.caregiver.bioQuestion.updateMultiple(caregiverRn, questions)
        .then((result) => {
          setBioQuestions(simplifyQuestions(result?.caregiverBio?.questions || {}));
          onSuccess();
        })
        .catch((error) => {
          console.error(error);
        });
  };

  const getSignedFileUrl = (bioQuestionId, file, isPublic=false, onSuccess=()=>undefined) => {
    const body = {
      fileDir: `${isPublic ? 'public/' : ''}caregiver/${caregiverRn}/${bioQuestionId}`,
      fileName: file.name,
      fileType: file.type,
    };

    return backend.fileStorage.uploadFile(body).then((result) => ({
      signedUrl: result.signedUrl,
      fileUrl: result.fileUrl,
      onUploadComplete: onSuccess,
    }));
  };

  const resolvedName = caregiver?.caregiverName || caregiverName;
  const sections = [
    ['Profile', Sections.ProfileView, Sections.ProfileEdit],
    ['Languages', Sections.LanguagesView, Sections.LanguagesEdit],
    ['Certificate', Sections.CertificateView, Sections.CertificateEdit],
    ['CanadianId', Sections.CanadianIdView, Sections.CanadianIdEdit],
    ['VulnerableSectorCheck', Sections.VulnerableSectorCheckView, Sections.VulnerableSectorCheckEdit],
    ['Education', Sections.EducationView, Sections.EducationEdit],
    ['WorkHistory', Sections.WorkHistoryView, Sections.WorkHistoryEdit],
    ['References', Sections.ReferencesView, Sections.ReferencesEdit],
    ['PrefsAndAvailability', Sections.PrefsAndAvailabilityView],
    ['SkillsAndExp', Sections.SkillsAndExpView],
    ['Personality', Sections.PersonalityView],
    ['Agreement', Sections.AgreementView],
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle onClose={onClose} sx={{pr: 6}}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h5" component="span" sx={{fontWeight: 700}}>
            {resolvedName}
          </Typography>
          {
            caregiver?.status &&
            <Chip
              label={translate(`caregiver.Status.${caregiver.status}.Title`)}
              size="small"
            />
          }
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{minHeight: 240, maxHeight: '80vh'}}>
        {
          isLoading && !bioQuestions &&
          <Stack alignItems="center" justifyContent="center" sx={{minHeight: 200}}>
            <CircularProgress />
          </Stack>
        }
        {
          hasError && !bioQuestions &&
          <Typography color="error" align="center" sx={{py: 8}}>
            <Translate expression="career.jobDetail.CaregiverQuickView.LoadError" />
          </Typography>
        }
        {
          bioQuestions &&
          <Stack spacing={2}>
            <ContactSection bioQuestions={bioQuestions} />
            {
              sections.map(([key, ViewComponent, EditComponent]) => (
                <ProfileSection
                  key={key}
                  bioQuestions={bioQuestions}
                  caregiverRn={caregiverRn}
                  EditComponent={EditComponent}
                  getSignedFileUrl={getSignedFileUrl}
                  saveBioQuestions={saveBioQuestions}
                  title={translate(`caregiver.CaregiverBiography.${key}.Title`)}
                  ViewComponent={ViewComponent}
                />
              ))
            }
          </Stack>
        }
      </DialogContent>
    </Dialog>
  );
};

CaregiverProfileDialog.propTypes = {
  caregiverName: PropTypes.string.isRequired,
  caregiverRn: PropTypes.string.isRequired,
  initialBio: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default React.memo(CaregiverProfileDialog);
